// src/transport/registry.ts
var GRAPHQL_MASTER_FLAG = "graphql.enabled";
function flagForOperation(op) {
  return `graphql.${op}`;
}
var TRANSPORT_CAPABILITY = {
  /** Route READS to the GraphQL fast path at all. */
  FAST_PATH: "transport.fastPath",
  /** Route IDEMPOTENT WRITES to GraphQL (strictly above {@link FAST_PATH}). */
  WRITES: "transport.writes",
  /**
   * Choose transport PER OPERATION rather than only via the master switch.
   *
   * Read by the settings UI, not by {@link explainTransport} — withholding it
   * hides the per-operation rows, it does not change where an operation routes.
   */
  PER_OPERATION: "transport.perOperation"
};
var TRANSPORT_REGISTRY = {
  "catalog.list": { default: "graphql", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  // The by-id point lookup. One row per alias, where the REST route reads many
  // ids in a single call — so the win is the SINGLE id (a catalog row opening
  // its own page). The caller still caps the set: past
  // `GRAPHQL_CATALOG_ID_LIMIT` this stays on REST whatever the default says,
  // because N aliases against one comma-joined read is the worse trade.
  "catalog.item": { default: "graphql", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  // The bounded "what is related to this?" walk. GraphQL-ONLY: private-api's
  // resolver is the only thing that can reach every table it touches, so REST
  // here means the caller renders the flat reverse-assignment list instead.
  // Registered anyway — an unregistered read is one the master kill-switch
  // cannot reach, which is exactly what it was.
  "catalog.related": {
    default: "graphql",
    graphqlOnly: true,
    capability: TRANSPORT_CAPABILITY.FAST_PATH
  },
  // The address LIST, as opposed to the per-address enrichment reads below.
  // Listed so the read the address tables are built on is switchable like every
  // other one, rather than being the single address path hardwired to REST.
  "address.list": { default: "rest", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  // The cross-chain LENS — a different query from `addresses` (which has no
  // `cross_chain` scope member), resolving every chain a string exists on plus
  // the activity rollup in one call, with each row's overlays riding along.
  //
  // The one thing to know before reading a row from it: `AddressRowWithCatalog`
  // has no `saved` and no `annotated`, so this projection fills both with the
  // defaults the REST adapter already applies to a row that arrives without them
  // (`saved` true, `annotated` false — see `addressCrossChain.ts`). Everything
  // the chain switcher branches on (`isVerified`, `isBeingMapped`) is carried by
  // `AddressRow` and answers identically on both transports.
  "address.crossChain": { default: "graphql", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  "address.overlays": { default: "graphql", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  "address.detail": { default: "graphql", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  // Both transaction reads are FIRST-PAINT ONLY: each renders ahead of the
  // canonical REST read and is superseded the moment that lands, so routing them
  // to GraphQL buys a faster first frame (the feed arrives with catalog already
  // resolved) and risks nothing the REST answer does not immediately correct.
  "transactions.recentFeed": { default: "graphql", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  "transactions.header": { default: "graphql", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  "address.assignments.list": { default: "graphql", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  "address.entityMapping.read": { default: "graphql", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  "address.memberships.read": { default: "graphql", capability: TRANSPORT_CAPABILITY.FAST_PATH },
  // "Index a transaction you have never seen." Listed as a write because it can
  // spend a provider call, and eligible because the resolver is IDEMPOTENT by
  // contract: an already-indexed hash comes back as-is with no second call, so a
  // retry after a fallback converges.
  //
  // Bounded by where it runs rather than by its default: the only caller fires
  // it AFTER a REST miss, so the provider call is spent on hashes the platform
  // could not otherwise answer for at all.
  "transactions.request": { default: "graphql", capability: TRANSPORT_CAPABILITY.WRITES },
  "address.assignments.write": { default: "graphql", capability: TRANSPORT_CAPABILITY.WRITES },
  "address.primary.write": { default: "graphql", capability: TRANSPORT_CAPABILITY.WRITES },
  "address.entityMapping.write": { default: "graphql", capability: TRANSPORT_CAPABILITY.WRITES },
  // The batch membership write is listed because it is idempotent PER MEMBER (each
  // upserts on its own deterministic (entity, validFrom) key), so a retry after a
  // partial write converges instead of double-mapping the members that landed.
  "address.memberships.write": { default: "graphql", capability: TRANSPORT_CAPABILITY.WRITES }
};
var DATA_OPERATIONS = Object.keys(TRANSPORT_REGISTRY);
function isDataOperation(value) {
  return typeof value === "string" && Object.hasOwn(TRANSPORT_REGISTRY, value);
}
function capabilityGranted(capability, source) {
  if (capability === void 0) return true;
  if (source === void 0 || source === true) return true;
  if (typeof source === "function") return source(capability);
  return source.has(capability);
}
function flagValue(flags, name) {
  if (flags.valueOf) return flags.valueOf(name);
  return flags.isEnabled(name) ? true : void 0;
}
function explainTransport(op, flags, options = {}) {
  if (options.graphqlConfigured === false) {
    return { transport: "rest", reason: "unconfigured", locked: true };
  }
  const entry = TRANSPORT_REGISTRY[op];
  if (!capabilityGranted(entry?.capability, options.capabilities)) {
    return { transport: "rest", reason: "not-entitled", locked: true };
  }
  if (!flags.isEnabled(GRAPHQL_MASTER_FLAG)) {
    return { transport: "rest", reason: "master-off", locked: false };
  }
  const perOp = flagValue(flags, flagForOperation(op));
  if (perOp !== void 0) {
    return { transport: perOp ? "graphql" : "rest", reason: "operation-flag", locked: false };
  }
  return { transport: entry.default, reason: "registry-default", locked: false };
}
function resolveTransport(op, flags, options = {}) {
  return explainTransport(op, flags, options).transport;
}
function usesGraphql(op, flags, options) {
  return resolveTransport(op, flags, options) === "graphql";
}

export { DATA_OPERATIONS, GRAPHQL_MASTER_FLAG, TRANSPORT_CAPABILITY, TRANSPORT_REGISTRY, explainTransport, flagForOperation, isDataOperation, resolveTransport, usesGraphql };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map